import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/shared/lib/supabase'
import { useScopingAIMessages, useAddScopingAIMessage } from './use-scoping-ai-messages'
import type { ScopingAIMessage, ScopingAIMessageRole } from '@/shared/types/database'

interface ProjectContext {
  name: string
  description?: string | null
  objective?: string | null
  targetUsers?: string | null
  existingAssets?: string | null
  constraints?: string | null
  inspirations?: string | null
}

interface SpecsUpdate {
  section: string
  content: string
}

interface ScopingAIResponse {
  message: string
  specs_update?: SpecsUpdate       // Single update (legacy)
  specs_updates?: SpecsUpdate[]    // Multiple updates (new)
}

interface UseScopingAIAssistantOptions {
  onSpecsUpdate?: (update: SpecsUpdate) => void
}

const MAX_REQUESTS = 100

/**
 * Hook for the AI scoping assistant (NicolAI v2)
 *
 * This hook manages the conversation with NicolAI during scoping phase.
 * It persists messages to the database and loads history on mount.
 *
 * @param projectId - The project ID
 * @param projectContext - Project context from pre-scoping
 * @param currentSpecs - Current specs content (markdown)
 * @param options - Optional callbacks
 */
export function useScopingAIAssistant(
  projectId: string | undefined,
  projectContext: ProjectContext | undefined,
  currentSpecs: string | undefined,
  options?: UseScopingAIAssistantOptions
) {
  const { i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  // Load messages from database
  const { data: dbMessages, isLoading: isLoadingHistory } = useScopingAIMessages(projectId)
  const addMessage = useAddScopingAIMessage()

  // Compute request count from database messages
  const requestCount = dbMessages?.filter(m => m.role === 'user').length || 0
  const isRateLimited = requestCount >= MAX_REQUESTS

  // Convert database messages to display format
  const messages: ScopingAIMessage[] = dbMessages || []

  // Initialize with welcome message if conversation is empty
  useEffect(() => {
    // Guard: wait for data to load
    if (!projectId || !projectContext || isLoadingHistory) return

    // Guard: already initialized in this session
    if (isInitialized) return

    // History exists → just mark as initialized, no welcome message needed
    if (dbMessages && dbMessages.length > 0) {
      setIsInitialized(true)
      return
    }

    // No history yet (dbMessages is empty array) → create welcome message
    // IMPORTANT: set initialized BEFORE async call to prevent race condition
    setIsInitialized(true)

    const initializeConversation = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const { data, error: fnError } = await supabase.functions.invoke<ScopingAIResponse>(
          'ai-scoping-assistant-claude',
          {
            body: {
              messages: [],
              projectContext: {
                name: projectContext.name,
                description: projectContext.description,
                objective: projectContext.objective,
                targetUsers: projectContext.targetUsers,
                existingAssets: projectContext.existingAssets,
                constraints: projectContext.constraints,
                inspirations: projectContext.inspirations,
              },
              currentSpecs,
              language: i18n.language.startsWith('fr') ? 'fr' : 'en',
            },
          }
        )

        if (fnError) {
          throw new Error(fnError.message)
        }

        if (data?.message) {
          // Get all specs updates (prefer new array format, fallback to legacy single)
          const allUpdates = data.specs_updates || (data.specs_update ? [data.specs_update] : [])

          // Save welcome message to database (store all updates)
          await addMessage.mutateAsync({
            projectId,
            role: 'assistant',
            content: data.message,
            specsUpdate: allUpdates.length > 0 ? { updates: allUpdates } as Record<string, unknown> : undefined,
          })

          // If there are specs updates (new project), notify parent for EACH update
          for (const update of allUpdates) {
            options?.onSpecsUpdate?.(update)
          }
        }
      } catch (err) {
        console.error('Error getting welcome message:', err)
        // Save fallback message
        const fallbackMessage =
          i18n.language === 'fr'
            ? `Bonjour ! 👋 Je suis NicolAI, votre Chef de Projet Fonctionnel pour ce cadrage de **${projectContext.name}**. Parlez-moi de votre projet !`
            : `Hello! 👋 I'm NicolAI, your Functional Project Manager for scoping **${projectContext.name}**. Tell me about your project!`

        await addMessage.mutateAsync({
          projectId,
          role: 'assistant',
          content: fallbackMessage,
        })
      } finally {
        setIsLoading(false)
      }
    }

    initializeConversation()
  }, [projectId, projectContext, dbMessages, isLoadingHistory, isInitialized, i18n.language, currentSpecs, addMessage])

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || !projectId || !projectContext || isLoading || isRateLimited) return

      setError(null)
      setIsLoading(true)

      try {
        // Save user message to database first
        await addMessage.mutateAsync({
          projectId,
          role: 'user',
          content: content.trim(),
        })

        // Prepare conversation history for API (include the new message)
        const conversationHistory = [
          ...(dbMessages || []),
          { role: 'user' as ScopingAIMessageRole, content: content.trim() },
        ].map((msg) => ({
          role: msg.role,
          content: msg.content,
        }))

        const { data, error: fnError } = await supabase.functions.invoke<ScopingAIResponse>(
          'ai-scoping-assistant-claude',
          {
            body: {
              messages: conversationHistory,
              projectContext: {
                name: projectContext.name,
                description: projectContext.description,
                objective: projectContext.objective,
                targetUsers: projectContext.targetUsers,
                existingAssets: projectContext.existingAssets,
                constraints: projectContext.constraints,
                inspirations: projectContext.inspirations,
              },
              currentSpecs,
              language: i18n.language.startsWith('fr') ? 'fr' : 'en',
            },
          }
        )

        if (fnError) {
          throw new Error(fnError.message)
        }

        if (data?.message) {
          // Get all specs updates (prefer new array format, fallback to legacy single)
          const allUpdates = data.specs_updates || (data.specs_update ? [data.specs_update] : [])

          // Save assistant response to database (store all updates)
          await addMessage.mutateAsync({
            projectId,
            role: 'assistant',
            content: data.message,
            specsUpdate: allUpdates.length > 0 ? { updates: allUpdates } as Record<string, unknown> : undefined,
          })

          // Notify parent of specs update for EACH update found
          for (const update of allUpdates) {
            options?.onSpecsUpdate?.(update)
          }
        }
      } catch (err) {
        console.error('Error sending message:', err)
        setError(
          i18n.language === 'fr'
            ? "Une erreur s'est produite. Veuillez réessayer."
            : 'An error occurred. Please try again.'
        )
      } finally {
        setIsLoading(false)
      }
    },
    [projectId, projectContext, dbMessages, currentSpecs, isLoading, isRateLimited, i18n.language, addMessage, options]
  )

  const retry = useCallback(() => {
    setError(null)
    // Get the last user message and resend
    const lastUserMessage = [...(dbMessages || [])].reverse().find((m) => m.role === 'user')
    if (lastUserMessage) {
      sendMessage(lastUserMessage.content)
    }
  }, [dbMessages, sendMessage])

  return {
    messages,
    isLoading: isLoading || isLoadingHistory,
    error,
    isRateLimited,
    requestCount,
    maxRequests: MAX_REQUESTS,
    sendMessage,
    retry,
  }
}
